import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Eye, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCatalogs, catalogMap } from "@/lib/lookups";
import { OPP_FIELDS, SEMAPHORE } from "@/lib/conecta";
import { fmtDate } from "@/lib/finance";
import { FieldsForm } from "@/components/finance/FieldsForm";
import { Timeline } from "@/components/finance/Timeline";
import { DetailFields, Section } from "@/components/common/DetailFields";
import { BackLink, PrivateBadge, ShareButtons } from "@/components/conecta/Bits";
import { CommissionsSection } from "@/components/conecta/CommissionsSection";
import { OnePagesSection } from "@/components/conecta/OnePagesSection";
import { ReferralCard } from "@/components/red/ReferralCard";
import { ClosedBadge } from "@/components/conecta/ClosedBadge";

const MirrorView = ({ o, onChanged }) => (
  <div data-testid="opportunity-mirror-view" className="space-y-4">
    <BackLink to="/conecta/negocios" label="Negocios" />
    <div className="space-y-1">
      <h2 data-testid="opportunity-detail-title" className="font-heading text-2xl font-extrabold tracking-tight">{o.title}</h2>
      <ClosedBadge o={o} />
      {o.description && <p className="text-sm c-text-2">{o.description}</p>}
      <p className="text-xs c-muted">Registro de solo lectura · solo puedes marcar el cierre</p>
    </div>
    <ReferralCard oppId={o.id} onChanged={onChanged} />
    <Section title="Línea de tiempo" testid="detail-timeline"><Timeline events={o.timeline} labels={{}} /></Section>
  </div>
);

const DETAIL_FIELDS = OPP_FIELDS.filter((f) => !["title", "stage", "semaphore"].includes(f.key) && !f.key.startsWith("resolver_collected"));

export default function OpportunityDetailPage() {
  const { id } = useParams();
  const { isOwner } = useAuth();
  const cats = useCatalogs();
  const [o, setO] = useState(null);
  const [editing, setEditing] = useState(false);
  const labels = useMemo(() => Object.fromEntries(OPP_FIELDS.map((f) => [f.key, f.label])), []);
  const load = useCallback(() => api.get(`/opportunities/${id}`).then((r) => setO(r.data)).catch((e) => { setO(false); toast.error(errMsg(e)); }), [id]);
  useEffect(() => { load(); }, [load]);

  const patch = async (payload, msg = "Cambios guardados") => {
    if (!Object.keys(payload).length) return true;
    try { setO((await api.patch(`/opportunities/${id}`, payload)).data); toast.success(msg); return true; } catch (e) { toast.error(errMsg(e)); return false; }
  };

  if (o === false) return <div className="space-y-3"><BackLink to="/conecta/negocios" label="Negocios" /><p data-testid="opportunity-not-found" className="c-card p-6 text-center c-text-2">Negocio no encontrado.</p></div>;
  if (!o) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin c-muted" /></div>;
  if (o.es_espejo) return <MirrorView o={o} onChanged={load} />;
  const stages = catalogMap(cats, "stages");
  const shareText = `${o.title}${o.next_step_text ? ` — Próximo paso: ${o.next_step_text}${o.next_step_date ? ` (${fmtDate(o.next_step_date)})` : ""}` : ""}`;

  return (
    <div data-testid="opportunity-detail-page" className="space-y-4">
      <BackLink to="/conecta/negocios" label="Negocios" />
      <div className="space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <h2 data-testid="opportunity-detail-title" className="font-heading text-2xl font-extrabold tracking-tight">{o.title}</h2>
          <PrivateBadge show={o.is_private_only_me} />
          <ClosedBadge o={o} />
        </div>
        <p className="text-sm c-text-2">{[o.names[o.requester_id], o.names[o.resolver_id]].filter(Boolean).join(" → ") || "Partes sin definir"}</p>
      </div>
      {o.origen === "red" && <ReferralCard oppId={o.id} onChanged={load} />}
      <OnePagesSection oppId={o.id} onChanged={load} />
      <div className="grid grid-cols-3 gap-2" data-testid="semaphore-selector">
        {Object.entries(SEMAPHORE).map(([k, s]) => (
          <button key={k} data-testid={`semaphore-${k}-btn`} onClick={() => patch({ semaphore: o.semaphore === k ? null : k }, "Semáforo actualizado")}
            className={`py-3 rounded-xl border text-sm font-bold transition-colors ${o.semaphore === k ? s.soft : "c-line c-muted"}`}>
            <span className={`inline-block w-3 h-3 rounded-full mr-1.5 align-middle ${s.dot}`} />{k[0].toUpperCase() + k.slice(1)}
          </button>
        ))}
      </div>
      <label className="block space-y-1">
        <span className="text-xs font-medium c-text-2">Etapa</span>
        <select data-testid="opportunity-stage-select" className="c-input font-semibold" value={o.stage} onChange={(e) => patch({ stage: e.target.value }, `Etapa: ${stages[e.target.value]}`)}>
          {Object.entries(stages).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </label>
      <div className={`c-card p-4 ${o.overdue ? "!border-red-500/60" : ""}`} data-testid="next-step-card">
        <p className="font-mono text-[10px] tracking-[0.2em] uppercase c-muted">Próximo paso</p>
        <p className="font-semibold mt-1">{o.next_step_text || "Sin definir"}</p>
        <p className={`text-sm ${o.overdue ? "text-red-600 dark:text-red-400 font-semibold" : "c-text-2"}`}>{fmtDate(o.next_step_date) || "Sin fecha"}{o.overdue ? " · vencido" : ""}</p>
      </div>
      <ShareButtons text={shareText} />
      {isOwner && o.type !== "del_despacho" && (
        <label className="flex items-center justify-between gap-3 c-card p-4 cursor-pointer" data-testid="amounts-visibility-toggle">
          <span className="flex items-center gap-2 text-sm"><Eye className="w-4 h-4 c-accent" /> Mostrar montos de comisión al asistente</span>
          <input type="checkbox" data-testid="amounts-visibility-checkbox" className="w-5 h-5 accent-[#bc9538]" checked={o.commission_amounts_visible}
            onChange={(e) => patch({ commission_amounts_visible: e.target.checked }, e.target.checked ? "Montos visibles para el asistente" : "Montos ocultos para el asistente")} />
        </label>
      )}
      <CommissionsSection opp={o} setOpp={setO} />
      <DetailFields fields={DETAIL_FIELDS} item={o} onEdit={() => setEditing(true)} />
      <Section title="Línea de tiempo" testid="detail-timeline"><Timeline events={o.timeline} labels={labels} /></Section>
      <FieldsForm open={editing} onOpenChange={setEditing} title="Editar negocio" fields={OPP_FIELDS} item={o} testid="edit-opportunity-sheet"
        onSubmit={async (p) => { if (await patch(p)) setEditing(false); }} />
    </div>
  );
}
